import {_, React, observer, observable, computed, IDisposer, action, useLocalStore, useEffect, useLayoutEffect, classNames, useAsObservableSource, computedFn, autorun, runInAction} from '@api'
import {CatalogItemsCardViewStore, useCatalogItemsCardViewStore, useVipLocalStore, VipLocalStore} from '@modules'
import {StoresInstance} from '@stores'
import {CollectionCellGroupRenderer, CollectionCellRenderer, CollectionCellSizeAndPositionGetter} from 'react-virtualized/dist/es/Collection'
import {CellRenderer, createCellPositioner} from 'react-virtualized/dist/es/Masonry'
import * as css from './VirtualizedProductView.css'
import {Masonry, AutoSizer, Grid, createMasonryCellPositioner, WindowScroller, CellMeasurerCache, CellMeasurer, Collection} from 'react-virtualized'

/// region Store

export class VirtualizedProductViewStore_Masonry extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { cvStore: CatalogItemsCardViewStore }) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = []

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  _cellMeasurementCache = new CellMeasurerCache({
    defaultHeight: 160,
    defaultWidth: 160,
    fixedWidth: false
  })

  _cellPositioner
  @observable columnWidth = 160
  @observable gutterSize = 10
  @observable width = 200
  _masonry: Masonry

  initCellPositioner = () => {
    if (typeof this._cellPositioner === 'undefined') {
      const {columnWidth, gutterSize, _cellMeasurementCache, columnCount} = this

      this._cellPositioner = createCellPositioner({
        cellMeasurerCache: _cellMeasurementCache,
        columnCount,
        columnWidth,
        spacer: gutterSize
      })
    }
  }

  _resetCellPositioner() {
    const {columnWidth, gutterSize, columnCount} = this

    this._cellPositioner.reset({
      columnCount,
      columnWidth,
      spacer: gutterSize
    })
  }

  _setMasonryRef = (ref) => {
    this._masonry = ref
  }

  @computed get columnCount() {
    const {columnWidth, gutterSize, width} = this

    return Math.floor(width / (columnWidth + gutterSize))
  }

  @action onResize = ({width}) => {
    this.width = width
    this._resetCellPositioner()
    this._masonry.recomputeCellPositions()
  }

  get cvStore() { return this.props.cvStore}

  _cellRenderer: CellRenderer = ({index, key, parent, style}) => {
    const {columnWidth, _cellMeasurementCache, cvStore: {virtualRows, itemsPerClick}} = this

    // console.log(`render cell`, {index, key})
    return (
      <CellMeasurer cache={_cellMeasurementCache} index={index} key={key} parent={parent}>
        <div
          className={css.cell}
          style={{
            ...style,
            width: columnWidth
          }}>
          <div className={css.cellContents} data-index={index}>
            {virtualRows[0].childrenCards[index % itemsPerClick]}
            {index % 10 === 0 && <div className={css.larger} style={{height: 50 + index / itemsPerClick}}>Extra Text</div>}
          </div>
        </div>
      </CellMeasurer>
    )
  }
}

export const VirtualizedProductViewMasonryStoreContext = React.createContext<VirtualizedProductViewStore_Masonry>(null)
export const useVirtualizedProductViewMasonryStore = () => React.useContext(VirtualizedProductViewMasonryStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const VirtualizedProductView_Masonry = observer<MyProps>(function VirtualizedProductView_Masonry({className, ...props}) {
  const cvStore = useCatalogItemsCardViewStore(),
    obsProps = useAsObservableSource({...props, cvStore}),
    store = useVipLocalStore((stores) => new VirtualizedProductViewStore_Masonry(stores, obsProps)),
    {onResize, _setMasonryRef, _cellMeasurementCache, _cellRenderer} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <VirtualizedProductViewMasonryStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <AutoSizer
          onResize={onResize}
          //overscanByPixels={overscanByPixels}
          //scrollTop={this._scrollTop}
        >
          {({height, width}) => {
            console.log({height, width})
            store.width = width
            store.initCellPositioner()

            return <Masonry
              cellCount={1000}
              autoHeight={false}
              cellMeasurerCache={_cellMeasurementCache}
              cellPositioner={store._cellPositioner}
              cellRenderer={_cellRenderer}
              height={height}
              //overscanByPixels={overscanByPixels}
              ref={_setMasonryRef}
              //scrollTop={this._scrollTop}
              width={width}
            />
          }}
        </AutoSizer>
      </div>
    </VirtualizedProductViewMasonryStoreContext.Provider>)
})

/// endregion

/// region Store

export class VirtualizedProductViewStore extends VipLocalStore {
  constructor(stores: StoresInstance, public props: Omit<MyProps, 'className'> & { cvStore: CatalogItemsCardViewStore }) {
    super(stores)
    stores.catalog.componentStores.virtualizer = this
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(() => {
        const {width, columnCount} = this
        runInAction(() => {
          const {collection} = this
          collection && collection.recomputeCellSizesAndPositions()
        })
      })
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  _cellRenderer = ({columnIndex, key, rowIndex, style}) => {
    const {cvStore: {virtualRows, itemsPerClick}, columnCount} = this,
      index = rowIndex * columnCount + columnIndex

    return (
      <div className={css.cell} key={key} style={style}>
        <div className={css.cellContents} data-index={index}>
          {virtualRows[0].childrenCards[index % itemsPerClick]}
        </div>
      </div>
    )
  }

  @computed get columnCount() {
    return Math.max(Math.floor(this.width / this.cellWidth), 1)
  }

  @observable width = 180

  get cvStore() { return this.props.cvStore}

  renderCollectionCell : CollectionCellRenderer = computedFn(({index, style, key}) => {
    const {cvStore: {virtualRows, itemsPerClick}} = this
    // console.log({index, style})
    return <div className={css.cell} style={style} key={key} data-index={index}>
      <div className={css.cellContents} data-index={index}>
        {virtualRows[0].childrenCards[index % itemsPerClick]}
      </div>
    </div>
  })

  // renderCollectionCellGroup: CollectionCellGroupRenderer = ({indices, cellSizeAndPositionGetter, cellRenderer}) => {
  //   const {cvStore: {virtualRows, itemsPerClick}} = this
  //   // console.log({index, style})
  //   return <div className={css.cell} style={style} key={key} data-index={index}>
  //     <div className={css.cellContents} data-index={index}>
  //       {virtualRows[0].childrenCards[index % itemsPerClick]}
  //     </div>
  //   </div>
  // }

  @computed get cellHeight() {
    const {catalog: {settings: {levelOfDetail}}} = this
    return levelOfDetail === 1 ? 821 : 160
  }

  @computed get cellWidth() {
    const {catalog: {settings: {levelOfDetail}}} = this
    return levelOfDetail === 1 ? 310 : 160
  }

  getCellSizeAndPosition : CollectionCellSizeAndPositionGetter = ({index, }) => {
    const {columnCount, width, cellHeight, cellWidth} = this
    const r = ({
      height: cellHeight,
      width: cellWidth,
      x: index % columnCount * cellWidth,
      y: Math.floor(index / columnCount) * cellHeight
    })
    // console.log({index, r, columnCount, width})
    return r
  }

  collection : Collection
  setCollectionRef = r => this.collection = r
}

export const VirtualizedProductViewStoreContext = React.createContext<VirtualizedProductViewStore>(null)
export const useVirtualizedProductViewStore = () => React.useContext(VirtualizedProductViewStoreContext)

/// endregion

/// region Components

interface MyProps {
  className?: string
}

export const VirtualizedProductView = observer<MyProps>(function VirtualizedProductView({className, ...props}) {
  const cvStore = useCatalogItemsCardViewStore(),
    obsProps = useAsObservableSource({...props, cvStore}),
    store = useVipLocalStore((stores) => new VirtualizedProductViewStore(stores, obsProps)),
    {renderCollectionCell, getCellSizeAndPosition, setCollectionRef} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <VirtualizedProductViewStoreContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        <AutoSizer>
          {({width, height}) => {
            store.width = width

            return width === 0 ? null : (
              <Collection
                ref={setCollectionRef}
                cellCount={cvStore.totalCount}
                cellRenderer={renderCollectionCell}
                cellSizeAndPositionGetter={getCellSizeAndPosition}
                height={height}
                width={width}
                sectionSize={100}
                // cellGroupRenderer={renderCollectionCellGroup}
              />

            // <Grid
            //     cellRenderer={_cellRenderer}
            //     className={css.grid}
            //     columnWidth={_getColumnWidth}
            //     columnCount={columnCount}
            //     height={height}
            //     // overscanColumnCount={overscanColumnCount}
            //     // overscanRowCount={overscanRowCount}
            //     rowHeight={_getRowHeight}
            //     rowCount={rowCount}
            //     width={width}
            //   />
            )
          }}
        </AutoSizer>
      </div>
    </VirtualizedProductViewStoreContext.Provider>)
})

/// endregion



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/CardView/LoadMoreProductCatalog/VirtualizedProductView.tsx